import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Loader2, AlertCircle, Building2, LogIn, Check, X, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';

interface ClientInfo {
  id: string;
  name: string;
  company?: string;
  email?: string;
  user_id: string; // event planner who owns this client
}

export const ClientPortalInvitePage: React.FC = () => {
  const { clientId } = useParams<{ clientId: string }>();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  
  const [clientInfo, setClientInfo] = useState<ClientInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [accepting, setAccepting] = useState(false);
  const [emailMismatch, setEmailMismatch] = useState(false);
  const [plannerName, setPlannerName] = useState<string>('');

  // Fetch client info using SECURITY DEFINER function (bypasses RLS for invitation validation)
  useEffect(() => {
    const loadClientInfo = async () => {
      if (!clientId) {
        setError('Invalid invitation link format. Please contact your event planner for a new link.');
        localStorage.removeItem('pending_client_portal_invite'); // Clear stale token to prevent redirect loops
        setLoading(false);
        return;
      }
      
      try {
        // Use SECURITY DEFINER function to fetch client info (bypasses RLS)
        const { data, error: clientError } = await supabase
          .rpc('get_client_for_invitation', { p_client_id: clientId });
        
        if (clientError) {
          console.error('Error fetching client:', clientError);
          setError('This invitation link is no longer valid. Please contact your event planner for a new link.');
          localStorage.removeItem('pending_client_portal_invite'); // Clear stale token to prevent redirect loops
          setLoading(false);
          return;
        }
        
        if (!data || data.length === 0) {
          setError('This invitation link is no longer valid. Please contact your event planner for a new link.');
          localStorage.removeItem('pending_client_portal_invite'); // Clear stale token to prevent redirect loops
          setLoading(false);
          return;
        }
        
        const clientData = data[0];
        setClientInfo(clientData);
        
        // Get event planner name
        const { data: plannerProfile } = await supabase
          .from('profiles')
          .select('display_name')
          .eq('user_id', clientData.user_id)
          .single();
        
        setPlannerName(plannerProfile?.display_name || 'Event Planner');
        
      } catch (err) {
        console.error('Error loading client info:', err);
        setError('Failed to load invitation details. Please try again or contact your event planner.');
        localStorage.removeItem('pending_client_portal_invite'); // Clear stale token to prevent redirect loops
      } finally {
        setLoading(false);
      }
    };

    loadClientInfo();
  }, [clientId]);

  // Check email match when user is authenticated
  useEffect(() => {
    if (user && clientInfo && clientInfo.email) {
      const userEmail = user.email?.toLowerCase();
      const clientEmail = clientInfo.email.toLowerCase();
      
      if (userEmail !== clientEmail) {
        setEmailMismatch(true);
      } else {
        setEmailMismatch(false);
      }
    }
  }, [user, clientInfo]);

  const handleGoToAuth = () => {
    // Store client ID in localStorage so it persists after auth
    if (clientId) {
      localStorage.setItem('pending_client_portal_invite', clientId);
    }
    navigate('/auth?redirect=/client-dashboard');
  };

  const sendNotificationEmail = async (response: 'accepted' | 'declined') => {
    if (!clientInfo) return;
    
    try {
      await supabase.functions.invoke('send-client-invitation-response', {
        body: {
          plannerUserId: clientInfo.user_id,
          plannerName: plannerName,
          clientName: clientInfo.name,
          clientCompany: clientInfo.company,
          clientUserName: user?.user_metadata?.display_name || user?.email?.split('@')[0] || 'Client',
          clientUserEmail: user?.email || '',
          response
        }
      });
      console.log(`Notification email sent for ${response} response`);
    } catch (error) {
      console.error('Failed to send notification email:', error);
      // Don't block the flow if email fails
    }
  };

  const handleAcceptInvitation = async () => {
    if (!clientId || !user || !clientInfo) return;

    try {
      setAccepting(true);
      
      // Use SECURITY DEFINER RPC function to safely bypass RLS
      const { data, error: rpcError } = await supabase
        .rpc('accept_client_portal_invitation', { p_client_id: clientId });

      if (rpcError) {
        console.error('RPC error:', rpcError);
        throw new Error('Failed to accept invitation. Please try again.');
      }

      // Cast response to expected shape
      const result = data as { success: boolean; error?: string; already_member?: boolean; member_id?: string } | null;

      // Handle RPC response
      if (!result || !result.success) {
        const errorCode = result?.error;
        if (errorCode === 'not_authenticated') {
          throw new Error('Please sign in to accept this invitation.');
        } else if (errorCode === 'client_not_found') {
          throw new Error('This invitation is no longer valid.');
        } else if (errorCode === 'email_mismatch') {
          setEmailMismatch(true);
          setAccepting(false);
          return;
        } else {
          throw new Error(result?.error || 'Failed to accept invitation');
        }
      }

      // Success!
      if (result.already_member) {
        toast.success('You already have access to this Client Portal');
      } else {
        toast.success(`You have been added as a client by ${plannerName}!`);
        // Send notification email to planner
        await sendNotificationEmail('accepted');
      }
      
      localStorage.removeItem('pending_client_portal_invite');
      navigate('/client-dashboard');
    } catch (error: any) {
      console.error('Error accepting invitation:', error);
      toast.error(error.message || 'Failed to accept invitation');
      setAccepting(false);
    }
  };

  const handleDeclineInvitation = async () => {
    // Send notification email to planner
    await sendNotificationEmail('declined');
    toast.info('Invitation declined');
    localStorage.removeItem('pending_client_portal_invite');
    navigate('/');
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    // Token remains in localStorage, user can sign in with correct email
  };

  // Show loading while checking auth state
  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted/50 p-4">
        <Card className="w-full max-w-md">
          <CardContent className="flex items-center justify-center p-8">
            <Loader2 className="h-6 w-6 animate-spin mr-2" />
            <span>Loading...</span>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Show error - NO dashboard access from error state
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted/50 p-4">
        <Card className="w-full max-w-md mx-4">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <img 
                src="https://fmmcpkpxsuxsqcxfwnch.supabase.co/storage/v1/object/public/mainlogob//ChatGPT_Image_Jul_21__2025__01_34_41_PM-removebg-preview.png" 
                alt="Event Date Picker Logo" 
                className="h-12 w-auto" 
              />
            </div>
          </CardHeader>
          <CardContent className="flex flex-col items-center justify-center p-8 text-center">
            <AlertCircle className="h-12 w-12 text-destructive mb-4" />
            <h2 className="text-lg font-semibold mb-2">Invalid Invitation Link</h2>
            <p className="text-muted-foreground mb-4">{error}</p>
            <div className="flex flex-col gap-3 w-full">
              <Button onClick={() => navigate('/auth')} variant="outline">
                <LogIn className="h-4 w-4 mr-2" />
                Sign In
              </Button>
              <Button onClick={() => navigate('/')} variant="ghost">
                Go to Home
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // If user is NOT authenticated, show sign-in prompt
  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted/50 p-4">
        <Card className="w-full max-w-md mx-4">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <img 
                src="https://fmmcpkpxsuxsqcxfwnch.supabase.co/storage/v1/object/public/mainlogob//ChatGPT_Image_Jul_21__2025__01_34_41_PM-removebg-preview.png" 
                alt="Event Date Picker Logo" 
                className="h-12 w-auto" 
              />
            </div>
            <div className="flex items-center justify-center mb-4">
              <div className="bg-primary/10 p-4 rounded-full">
                <Building2 className="h-8 w-8 text-primary" />
              </div>
            </div>
            <CardTitle className="text-xl">Client Portal Invitation</CardTitle>
            <CardDescription>
              You've been invited to access a Client Portal!
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="bg-muted/50 rounded-lg p-4 text-center space-y-2">
              <p className="font-medium text-foreground">
                You have been invited to access the Client Portal for <strong>{clientInfo?.name}</strong>
                {clientInfo?.company && <span className="text-muted-foreground"> ({clientInfo.company})</span>}
              </p>
              <p className="text-sm text-muted-foreground">
                Invited by: <strong>{plannerName}</strong>
              </p>
              {clientInfo?.email && (
                <p className="text-sm text-muted-foreground">
                  Please sign in using: <strong>{clientInfo.email}</strong>
                </p>
              )}
            </div>
            <Button onClick={handleGoToAuth} className="w-full" size="lg">
              <LogIn className="h-4 w-4 mr-2" />
              Sign In / Create Account
            </Button>
            <p className="text-xs text-center text-muted-foreground">
              After signing in, you'll have access to your Client Portal
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // If user's email doesn't match the client's email
  if (emailMismatch && clientInfo?.email) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted/50 p-4">
        <Card className="w-full max-w-md mx-4">
          <CardHeader className="text-center">
            <div className="flex items-center justify-center mb-4">
              <div className="bg-destructive/10 p-4 rounded-full">
                <AlertTriangle className="h-8 w-8 text-destructive" />
              </div>
            </div>
            <CardTitle className="text-xl">Email Mismatch</CardTitle>
            <CardDescription>
              This invitation was sent to a different email address
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="bg-destructive/10 rounded-lg p-4 text-center space-y-2">
              <p className="text-sm text-foreground">
                You signed in with: <strong>{user.email}</strong>
              </p>
              <p className="text-sm text-foreground">
                This invitation was sent to: <strong>{clientInfo.email}</strong>
              </p>
            </div>
            <p className="text-sm text-muted-foreground text-center">
              Please sign out and sign in with the invited email address to access this Client Portal.
            </p>
            <div className="flex flex-col gap-3">
              <Button onClick={handleSignOut} variant="outline" className="w-full">
                Sign Out & Switch Account
              </Button>
              <Button onClick={() => navigate('/')} variant="ghost" className="w-full">
                Go to Home
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // User is authenticated and (email matches OR no email restriction)
  return (
    <div className="min-h-screen flex items-center justify-center bg-muted/50 p-4">
      <Card className="w-full max-w-md mx-4">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <img 
              src="https://fmmcpkpxsuxsqcxfwnch.supabase.co/storage/v1/object/public/mainlogob//ChatGPT_Image_Jul_21__2025__01_34_41_PM-removebg-preview.png" 
              alt="Event Date Picker Logo" 
              className="h-12 w-auto" 
            />
          </div>
          <div className="flex items-center justify-center mb-4">
            <div className="bg-primary/10 p-4 rounded-full">
              <Building2 className="h-8 w-8 text-primary" />
            </div>
          </div>
          <CardTitle className="text-xl">Client Portal Invitation</CardTitle>
          <CardDescription>
            You've been invited to access a Client Portal
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="bg-muted/50 rounded-lg p-4 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Client</span>
              <span className="font-semibold">{clientInfo?.name}</span>
            </div>
            {clientInfo?.company && (
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Company</span>
                <span className="font-medium">{clientInfo.company}</span>
              </div>
            )}
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Invited By</span>
              <span className="font-medium">{plannerName}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Your Role</span>
              <Badge variant="default">Client</Badge>
            </div>
          </div>

          <p className="text-sm text-muted-foreground text-center">
            By accepting, you'll have full access to manage events, view feedback, and collaborate with your event planner.
          </p>

          <div className="flex flex-col sm:flex-row gap-3">
            <Button
              variant="outline"
              className="flex-1"
              onClick={handleDeclineInvitation}
              disabled={accepting}
            >
              <X className="h-4 w-4 mr-2" />
              Decline
            </Button>
            <Button
              className="flex-1"
              onClick={handleAcceptInvitation}
              disabled={accepting}
            >
              {accepting ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Check className="h-4 w-4 mr-2" />
              )}
              Accept
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ClientPortalInvitePage;
